import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { api } from "../lib/api";
import { useCompany } from "../lib/company";
import { Button, Card, Icon, JurisBadge, cx } from "../components/ui";
import { MonitoringNav } from "../components/MonitoringNav";
import { policyChangeTypeLabel, policyChangeTypeToneClass } from "../domain/policyReview";
import { formatAuDate } from "../domain/time";
import { formatCountLabel } from "../domain/text";
import type { ChangeType } from "../../convex/model/enums";

type UpdateRow = FunctionReturnType<typeof api.updateLog.recentForCompany>[number];
type UpdateFilter = "all" | "mapping" | "source";

export function Updates() {
  const navigate = useNavigate();
  const { companyId } = useCompany();
  const updates = useQuery(api.updateLog.recentForCompany, companyId !== null ? { companyId, limit: 30 } : "skip");
  const updateRows = useMemo(() => updates ?? [], [updates]);
  const [filter, setFilter] = useState<UpdateFilter>("all");
  const filteredRows = useMemo(() => updateRows.filter((update) => {
    const hasMappingImpact = (update.affectedPolicies?.length ?? 0) > 0;
    if (filter === "mapping") return hasMappingImpact;
    if (filter === "source") return !hasMappingImpact;
    return true;
  }), [filter, updateRows]);
  const loading = updates === undefined;

  if (companyId === null) {
    return <p className="px-9 py-7 text-slate-500">Preparing update workspace...</p>;
  }

  return (
    <section className="mx-auto w-full max-w-[1180px] px-4 py-5 sm:px-6 lg:px-9" aria-labelledby="updates-heading">
      <header className="flex flex-wrap items-start justify-between gap-5 border-b border-[var(--line)] pb-5">
        <div className="max-w-[760px]">
          <h1 id="updates-heading" className="font-serif text-[26px] font-semibold tracking-[-0.025em] text-slate-950">Updates</h1>
          <p className="mt-1.5 text-[13.5px] leading-relaxed text-slate-600">
            Recent legislative changes, with links to affected documents and source material.
          </p>
        </div>
        <Button variant="outline" size="sm" icon="search" onClick={() => navigate("/browse")}>Open source register</Button>
      </header>

      <div className="my-4"><MonitoringNav active="updates" /></div>

      <Card className="overflow-hidden border-[var(--line)]">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--line)] bg-slate-50/90 px-4 py-3 sm:px-5">
          <h2 className="text-[14px] font-semibold text-slate-950">Latest updates</h2>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <UpdateFilters value={filter} onChange={setFilter} />
            <div className="text-[12px] text-slate-500" aria-live="polite">
              {loading ? "Loading..." : `${formatCountLabel(filteredRows.length, "update")}${filter === "all" ? "" : ` of ${updateRows.length}`}`}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="px-5 py-10 text-sm text-slate-500" role="status">Loading legislative updates...</div>
        ) : updateRows.length === 0 ? (
          <EmptyUpdates onFindObligations={() => navigate("/browse")} />
        ) : filteredRows.length === 0 ? (
          <EmptyFilter onClear={() => setFilter("all")} />
        ) : (
          <ol className="divide-y divide-[var(--line)]">
            {filteredRows.map((update) => (
              <UpdateFeedRow
                key={update._id}
                update={update}
                affectedPolicies={update.affectedPolicies ?? []}
                affectedObligationSets={update.affectedObligationSetNames ?? []}
                onReview={(path) => navigate(path)}
                onOpenSource={() => {
                  const sectionId = update.appliesToSectionIds?.[0] ?? null;
                  navigate("/legislation/" + update.legislationId + (sectionId === null ? "" : "?section=" + sectionId));
                }}
              />
            ))}
          </ol>
        )}
      </Card>
    </section>
  );
}

function UpdateFilters({ value, onChange }: { value: UpdateFilter; onChange: (filter: UpdateFilter) => void }) {
  const options: readonly { value: UpdateFilter; label: string }[] = [
    { value: "all", label: "All updates" },
    { value: "mapping", label: "Mapping impact" },
    { value: "source", label: "Source only" },
  ];

  return (
    <div className="inline-flex flex-wrap border border-[var(--line)] bg-white p-0.5" role="group" aria-label="Filter legislative updates">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          className={cx(
            "px-2.5 py-1 text-[11.5px] font-medium transition",
            value === option.value
              ? "bg-[var(--accent-soft)] font-semibold text-[var(--accent-dark)]"
              : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
          )}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function UpdateFeedRow({
  update,
  affectedPolicies,
  affectedObligationSets,
  onReview,
  onOpenSource,
}: {
  update: UpdateRow;
  affectedPolicies: readonly AffectedPolicy[];
  affectedObligationSets: readonly string[];
  onReview: (path: string) => void;
  onOpenSource: () => void;
}) {
  const primaryReview = affectedPolicies[0] ?? null;
  return (
    <li className="grid gap-4 px-4 py-4 sm:px-5 lg:grid-cols-[minmax(0,1fr)_18rem_12rem] lg:gap-5">
      <div className="min-w-0">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          {update.legislation && <JurisBadge code={update.legislation.jurisdiction} />}
          <ChangeBadge type={update.changeType} />
          <span className="text-[11.5px] text-slate-500">Commences {formatAuDate(update.commencementDate)}</span>
        </div>
        <h3 className="text-[14px] font-semibold leading-snug text-slate-950">{update.legislation?.title ?? "Legislation"}</h3>
        <p className="mt-1.5 text-[13px] leading-relaxed text-slate-600">{update.summary ?? update.amendingLegislation}</p>
        <div className="mt-2 min-w-0 truncate border-l-2 border-[var(--line)] pl-3 text-[12px] text-slate-500" title={update.amendingLegislation}>{update.amendingLegislation}</div>
      </div>

      <div
        className={cx(
          "border-l-2 px-3 py-0.5 text-[12px] leading-snug",
          affectedPolicies.length > 0 ? "border-[var(--accent)]" : "border-[var(--line)]",
        )}
      >
        <div className="flex items-center justify-between gap-3">
          <div className={cx("text-[12px] font-semibold", affectedPolicies.length > 0 ? "text-[var(--accent-dark)]" : "text-slate-500")}>Mapping impact</div>
          {affectedPolicies.length > 0 && <span className="text-[11.5px] text-slate-500">{affectedPolicies.length} {affectedPolicies.length === 1 ? "document" : "documents"}</span>}
        </div>
        {affectedPolicies.length === 0 ? (
          <div className="mt-1.5 text-slate-500">No mapped documents detected.</div>
        ) : (
          <ul className="mt-1.5 space-y-1">{affectedPolicies.map((policy) => (
            <li key={policy.path} className="flex items-start gap-1.5">
              <Icon name="layers" size={13} className="mt-0.5 shrink-0 text-[var(--accent)]" />
              <button type="button" className="text-left font-semibold text-[var(--accent-dark)] underline-offset-2 hover:underline" onClick={() => onReview(policy.path)}>{policy.name}</button>
            </li>
          ))}</ul>
        )}

        {affectedObligationSets.length > 0 && (
          <div className="mt-2 text-[11.5px] text-slate-500"><span className="font-medium text-slate-600">Obligation sets:</span> {affectedObligationSets.join(", ")}</div>
        )}
      </div>

      <div className="flex flex-wrap items-start gap-2 lg:flex-col lg:items-stretch">
        {primaryReview !== null && (
          <Button size="sm" icon="layers" onClick={() => onReview(primaryReview.path)}>Review mapping impact</Button>
        )}
        <Button size="sm" variant="outline" icon="book" onClick={onOpenSource}>Open source</Button>
      </div>
    </li>
  );
}

function ChangeBadge({ type }: { type: ChangeType }) {
  return (
    <span className={cx("rounded px-1.5 py-0.5 text-[11px] font-semibold ring-1", policyChangeTypeToneClass(type))}>
      {policyChangeTypeLabel(type)}
    </span>
  );
}

function EmptyUpdates({ onFindObligations }: { onFindObligations: () => void }) {
  return (
    <div className="px-5 py-14 text-center">
      <div className="mx-auto mb-3 grid h-11 w-11 place-items-center rounded border border-[var(--line)] bg-slate-50 text-slate-500"><Icon name="bell" size={20} /></div>
      <div className="text-sm font-semibold text-slate-800">No legislative updates yet</div>
      <div className="mx-auto mt-1 max-w-md text-[13px] text-slate-500">New legislative updates will appear here with any affected obligations and mapped documents.</div>
      <Button size="sm" variant="outline" className="mt-4" icon="search" onClick={onFindObligations}>Open source register</Button>
    </div>
  );
}

function EmptyFilter({ onClear }: { onClear: () => void }) {
  return (
    <div className="px-5 py-12 text-center">
      <div className="text-sm font-semibold text-slate-800">No updates match this filter</div>
      <div className="mt-1 text-[13px] text-slate-500">The full list still contains updates.</div>
      <Button size="sm" variant="outline" className="mt-4" onClick={onClear}>Show all updates</Button>
    </div>
  );
}

type AffectedPolicy = UpdateRow["affectedPolicies"][number];
